import { useCallback, useEffect, useState } from "react";
import { HQ } from "./sportConfig";
import useViewPreference from "./useViewPreference";

export default function useLiveStandings(regular, competitionId, active = true) {
  const [preference, savePreference] = useViewPreference("sports-hq-standings-live", "0");
  const enabled = preference === "1";
  const identity = String(competitionId || "");
  const [snapshot, setSnapshot] = useState(null);
  const [state, setState] = useState({ identity: "", loading: false, error: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled || !active || !identity) { setSnapshot(null); return undefined; }
    let disposed = false, controller = null, busy = false;
    const refresh = async () => {
      if (document.hidden || disposed || busy) return;
      busy = true; const current = new AbortController(); controller = current;
      let timedOut = false;
      const timeout = setTimeout(() => { timedOut = true; current.abort(); }, 12000);
      setState({ identity, loading: true, error: false });
      try {
        const query = new URLSearchParams({ competition_id: identity, live: "true" });
        const response = await fetch(`${HQ.dataApi}/standings?${query}`, { signal: current.signal });
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (data.mode !== "live" || !data.standings?.length) throw new Error();
        if (!disposed && !current.signal.aborted) { setSnapshot({ identity, data, receivedAt: Date.now() }); setState({ identity, loading: false, error: false }); }
      } catch (error) {
        if (!disposed && (timedOut || !current.signal.aborted) && (timedOut || error.name !== "AbortError")) { setSnapshot(null); setState({ identity, loading: false, error: true }); }
      } finally { clearTimeout(timeout); if (controller === current) busy = false; }
    };
    const visibility = () => {
      if (document.hidden) { controller?.abort(); controller = null; busy = false; setSnapshot(null); }
      else refresh();
    };
    refresh();
    const interval = setInterval(refresh, 15000);
    document.addEventListener("visibilitychange", visibility);
    return () => { disposed = true; controller?.abort(); clearInterval(interval); document.removeEventListener("visibilitychange", visibility); };
  }, [enabled, identity, active, attempt]);
  const toggle = useCallback(() => savePreference(enabled ? "0" : "1"), [enabled, savePreference]);
  const data = enabled && active && snapshot?.identity === identity && Date.now() - snapshot.receivedAt <= 30000 ? snapshot.data : regular;
  return { data, enabled, toggle, loading: enabled && state.identity === identity && state.loading,
    error: enabled && state.identity === identity && state.error,
    retry: () => setAttempt(value => value + 1), live: enabled && data?.mode === "live" };
}
